// Deep imports only: the @/src/lib/filters barrel pulls client-only providers into the RSC graph.
import type { AutocompleteResult } from '@/src/lib/api/endpoints/area';
import type { RecentSearchAreaInfo, RecentSearchData } from '@/src/lib/api/endpoints/user';
import { deserializeFromUrl } from '@/src/lib/filters/filterSerializer';
import type { FilterState } from '@/src/lib/filters/filterTypes';
import { savedSearchFilterToQuery } from '@/src/lib/savedSearches';

// This app's SRP slugs are generic (impose no filters), so slug-implied suppression would lose real restrictions.
const NO_SLUG = '';

const COMMUTE_QUERY_KEYS = [
  'commuteLats',
  'commuteLons',
  'commuteTime',
  'commuteMode',
  'commuteTimeMode',
  'commuteName',
] as const;

// Recent searches store the full bedrooms list (no saved-search truncation): only a trailing 8plus is open-ended.
function bedroomsFromCsv(value: unknown): FilterState['bedrooms'] | null {
  if (value === null || value === undefined) return null;
  const values = String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  if (values.length === 0) return null;

  const first = values[0];
  const last = values[values.length - 1];
  if (first === '8plus') return { min: 8, max: null };

  const min = Number.parseInt(first, 10);
  if (Number.isNaN(min) || min < 0) return null;
  if (values.length === 1) return { min, max: min };
  if (last === '8plus') return min === 0 ? null : { min, max: null };

  const max = Number.parseInt(last, 10);
  if (Number.isNaN(max) || max < min) return { min, max: null };
  return { min, max };
}

function localDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// A recent search can be arbitrarily old; a bygone move-in window would silently skew SRP results.
function dropPastMoveInDates(state: FilterState): void {
  const today = localDateString(new Date());
  if (state.moveInDate.end && state.moveInDate.end < today) {
    state.moveInDate = { start: null, end: null };
  } else if (state.moveInDate.start && state.moveInDate.start < today) {
    state.moveInDate = { start: null, end: state.moveInDate.end };
  }
}

export function recentSearchToFilterState(recent: RecentSearchData | null | undefined): FilterState | null {
  const filter = recent?.filter;
  if (!filter || Object.keys(filter).length === 0) {
    return null;
  }

  const query = savedSearchFilterToQuery(filter, NO_SLUG);
  // Beds are mapped directly below; the saved-search heuristic assumes truncated lists.
  delete query.beds;
  for (const key of COMMUTE_QUERY_KEYS) {
    const value = filter[key];
    if (value !== undefined && value !== null && value !== '') {
      query[key] = String(value);
    }
  }

  const state = deserializeFromUrl(query);
  const bedrooms = bedroomsFromCsv(filter.bedrooms);
  if (bedrooms) {
    state.bedrooms = bedrooms;
  }
  dropPastMoveInDates(state);
  return state;
}

// Mirrors legacy areaUtils_getFullNameFromArea so the seeded label matches area names elsewhere in the app.
function areaDisplayName({ name, city, state, type }: RecentSearchAreaInfo): string | null {
  if (!name) {
    return city && state ? `${city}, ${state}` : null;
  }
  switch (type) {
    case 'city':
      return state ? `${name}, ${state}` : name;
    case 'neighborhood':
      return [name, city, state].filter(Boolean).join(', ');
    case 'county': {
      const suffix = name.split(' ').pop() === 'County' ? '' : ' County';
      return state ? `${name}${suffix}, ${state}` : `${name}${suffix}`;
    }
    default:
      return name;
  }
}

export function recentSearchToArea(recent: RecentSearchData | null | undefined): AutocompleteResult | null {
  const areaInfo = recent?.areaInfo;
  if (!areaInfo?.resourceId) {
    return null;
  }

  const name = areaDisplayName(areaInfo);
  if (!name) {
    return null;
  }

  // city/state omitted: formatAreaLabel would prefer "City, ST" and mislabel neighborhoods as their parent city.
  return {
    type: areaInfo.type ?? 'city',
    name,
    resourceId: areaInfo.resourceId,
  };
}
